import { useEffect, useMemo, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  FileDown,
  FileSpreadsheet,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useCompanies, useMappingTemplates } from "~/hooks/contabilidad";
import { useBudgetsByCompanyYear } from "~/hooks/contabilidad/useBudgets";
import { useBudgetVsActual } from "~/hooks/contabilidad/useBudgetVsActual";
import { useToast } from "~/hooks/use-toast";
import { exportBudgetVsActualToExcel } from "~/services/contabilidad/budgetVsActualExportService";
import {
  budgetVsActualService,
  getCumplimiento,
} from "~/services/contabilidad/budgetVsActualService";
import { BUDGET_STATUS_LABELS } from "~/types/contabilidad";

import { BudgetVsActualTable } from "./components/BudgetVsActualTable";

import type { BudgetVsActualScope } from "./components/BudgetVsActualTable";
import type { BudgetVsActualValues } from "~/types/contabilidad";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPct(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)}%`;
}

export default function BudgetVsActualPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const currentYear = dayjs().year();
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let year = 2024; year <= currentYear + 1; year++) years.push(year);
    return years;
  }, [currentYear]);

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [templateFilter, setTemplateFilter] = useState<string>("");
  const [budgetId, setBudgetId] = useState<string>("");
  const [scope, setScope] = useState<BudgetVsActualScope>("acumulado");
  const [isExporting, setIsExporting] = useState(false);

  const { data: companies = [] } = useCompanies();
  const { data: templates = [] } = useMappingTemplates({
    company_id: companyFilter,
    is_active: true,
  });
  const { data: budgets = [], isLoading: isLoadingBudgets } =
    useBudgetsByCompanyYear(companyFilter, year);
  const selectedBudget = budgets.find((b) => b.id === budgetId) ?? null;

  const { data: lines = [], isLoading } = useBudgetVsActual({
    year,
    month,
    companyId: companyFilter,
    templateId: templateFilter,
    budgetId,
  });

  const groupedData = useMemo(() => {
    if (lines.length === 0) return null;
    return budgetVsActualService.groupBudgetVsActualData(lines);
  }, [lines]);

  // Reset template filter when company changes (templates are per-company)
  const handleCompanyChange = (value: string) => {
    setCompanyFilter(value);
    setTemplateFilter("");
  };

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && !companyFilter) {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      } else if (companies.length === 1) {
        setCompanyFilter(companies[0].id);
      }
    }
  }, [companies, companyFilter]);

  // Auto-select first active template when company changes and templates load
  useEffect(() => {
    if (templates.length > 0 && !templateFilter && companyFilter) {
      setTemplateFilter(templates[0].id);
    }
  }, [templates, templateFilter, companyFilter]);

  // Default the budget selector to the first scenario; reset when the current
  // selection is no longer in the list (company/year changed).
  useEffect(() => {
    if (budgets.length === 0) {
      if (budgetId !== "") setBudgetId("");
      return;
    }
    if (!budgets.some((b) => b.id === budgetId)) {
      setBudgetId(budgets[0].id);
    }
  }, [budgets, budgetId]);

  const selectedTemplate = templates.find((t) => t.id === templateFilter);
  const selectedCompany = companies.find((c) => c.id === companyFilter);

  const handleExport = () => {
    if (!groupedData) return;

    setIsExporting(true);
    try {
      exportBudgetVsActualToExcel({
        data: groupedData,
        year,
        month,
        companyName: selectedCompany?.name || "",
        templateName: selectedTemplate?.name || "",
        budgetName: selectedBudget?.name || "Sin presupuesto",
      });

      toast({
        title: "Reporte exportado",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting Budget vs Actual:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getPair = (values: BudgetVsActualValues) =>
    scope === "mes"
      ? { budget: values.budgetMes, real: values.realMes }
      : { budget: values.budgetAcumulado, real: values.realAcumulado };

  const scopeLabel = scope === "mes" ? "Mes" : "Acumulado";

  const summaryCards = useMemo(() => {
    if (!groupedData) return null;

    const ingresos = getPair(groupedData.totals.ingresos);
    const costos = getPair(groupedData.totals.costos);
    const gastos = getPair(groupedData.totals.gastos);
    const utilidad = getPair(groupedData.totals.utilidadOperacional);

    const costosYGastos = {
      budget: costos.budget + gastos.budget,
      real: costos.real + gastos.real,
    };

    return { ingresos, costosYGastos, utilidad };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedData, scope]);

  return (
    <PageContainer>
      <PageHeader
        title="Presupuesto vs Real"
        description="Comparación del estado de resultados contra el presupuesto por cuenta"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/contabilidad/reportes")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button
            className="h-9"
            onClick={handleExport}
            disabled={!groupedData || isLoading || isExporting}
          >
            <FileDown className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Año</Label>
              <Select
                value={year.toString()}
                onValueChange={(value) => setYear(parseInt(value))}
              >
                <SelectTrigger className="h-9 w-full sm:w-[140px]">
                  <Calendar className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((yearOption) => (
                    <SelectItem key={yearOption} value={yearOption.toString()}>
                      {yearOption}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">Mes</Label>
              <Select
                value={month.toString()}
                onValueChange={(value) => setMonth(parseInt(value))}
              >
                <SelectTrigger className="h-9 w-full sm:w-[150px]">
                  <Calendar className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((monthName, index) => (
                    <SelectItem key={index} value={(index + 1).toString()}>
                      {monthName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Empresa <span className="text-red-500">*</span>
              </Label>
              <Select value={companyFilter} onValueChange={handleCompanyChange}>
                <SelectTrigger className="h-9 w-full sm:w-[200px]">
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                Plantilla de Mapeo <span className="text-red-500">*</span>
              </Label>
              <Select
                value={templateFilter}
                onValueChange={setTemplateFilter}
                disabled={!companyFilter}
              >
                <SelectTrigger className="h-9 w-full sm:w-[240px]">
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Seleccionar plantilla" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {budgets.length > 0 && (
              <div className="flex flex-col gap-2 sm:ml-auto">
                <Label className="text-sm font-medium">Presupuesto</Label>
                <div className="flex items-center gap-2">
                  <Select value={budgetId} onValueChange={setBudgetId}>
                    <SelectTrigger className="h-9 w-full sm:w-[220px]">
                      <SelectValue placeholder="Seleccionar presupuesto" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgets.map((budgetOption) => (
                        <SelectItem key={budgetOption.id} value={budgetOption.id}>
                          {budgetOption.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedBudget && (
                    <Badge
                      variant={
                        selectedBudget.status === "approved"
                          ? "default"
                          : "secondary"
                      }
                      className={
                        selectedBudget.status === "approved"
                          ? "bg-green-100 text-green-800 hover:bg-green-100"
                          : undefined
                      }
                    >
                      {BUDGET_STATUS_LABELS[selectedBudget.status]}
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {companyFilter && !isLoadingBudgets && budgets.length === 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Sin presupuesto</AlertTitle>
          <AlertDescription>
            No existe un presupuesto para {selectedCompany?.name || "la empresa"}{" "}
            en {year}. Las columnas de presupuesto se mostrarán en 0.{" "}
            <Link
              to="/contabilidad/presupuestos"
              className="font-medium underline underline-offset-4"
            >
              Crear presupuesto
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {groupedData && groupedData.unmapped.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-900 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            Cuentas con presupuesto sin clasificación en la plantilla
          </AlertTitle>
          <AlertDescription>
            <p className="mb-1">
              Estas cuentas tienen presupuesto pero no están mapeadas en la
              plantilla seleccionada, por lo que no aparecen en el reporte:
            </p>
            <ul className="list-inside list-disc">
              {groupedData.unmapped.map((line) => (
                <li key={line.account_id}>
                  <span className="font-mono">{line.account_code}</span> —{" "}
                  {line.account_name} (presupuesto acumulado:{" "}
                  {formatCurrency(Math.abs(line.budget_acumulado))})
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {!companyFilter || !templateFilter ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground">
              Selecciona una empresa y una plantilla de mapeo para ver el
              reporte.
            </p>
            <p className="text-sm text-muted-foreground">
              Ambos campos son requeridos para generar la comparación.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {summaryCards && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Ingresos ({scopeLabel})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summaryCards.ingresos.real)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Ppto: {formatCurrency(summaryCards.ingresos.budget)} ·{" "}
                    {formatPct(
                      getCumplimiento(
                        summaryCards.ingresos.real,
                        summaryCards.ingresos.budget
                      )
                    )}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Costos y Gastos ({scopeLabel})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summaryCards.costosYGastos.real)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Ppto: {formatCurrency(summaryCards.costosYGastos.budget)} ·{" "}
                    {formatPct(
                      getCumplimiento(
                        summaryCards.costosYGastos.real,
                        summaryCards.costosYGastos.budget
                      )
                    )}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Utilidad Operacional ({scopeLabel})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summaryCards.utilidad.real)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Ppto: {formatCurrency(summaryCards.utilidad.budget)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Desviación Utilidad ({scopeLabel})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className={`text-2xl font-bold ${
                      summaryCards.utilidad.real - summaryCards.utilidad.budget <
                      0
                        ? "text-red-700"
                        : "text-green-700"
                    }`}
                  >
                    {formatCurrency(
                      summaryCards.utilidad.real - summaryCards.utilidad.budget
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Cumplimiento:{" "}
                    {formatPct(
                      getCumplimiento(
                        summaryCards.utilidad.real,
                        summaryCards.utilidad.budget
                      )
                    )}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>
                    Presupuesto vs Real
                    {selectedTemplate && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        (Plantilla: {selectedTemplate.name})
                      </span>
                    )}
                  </CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {MONTH_NAMES[month - 1]} {year} —{" "}
                    {scope === "mes"
                      ? "solo el mes seleccionado"
                      : "acumulado desde enero al cierre del mes"}
                  </p>
                </div>
                <Tabs
                  value={scope}
                  onValueChange={(value) =>
                    setScope(value as BudgetVsActualScope)
                  }
                >
                  <TabsList className="grid w-full grid-cols-2 sm:w-[220px]">
                    <TabsTrigger value="mes">Mes</TabsTrigger>
                    <TabsTrigger value="acumulado">Acumulado</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              <BudgetVsActualTable
                data={groupedData}
                isLoading={isLoading}
                scope={scope}
              />
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  );
}
